import React, { useEffect, useState } from 'react';

export interface WorldEvent {
  id: string;
  type: 'pirate_raid' | 'rift_storm' | 'sea_monster' | 'weather' | 'territory_claim' | 'crew_event';
  title: string;
  description: string;
  severity: 'minor' | 'moderate' | 'major' | 'catastrophic';
  timestamp: number;
  expiresAt?: number;
  location?: { x: number; y: number };
}

interface WorldEventNotificationProps {
  events: WorldEvent[];
  onRemove: (eventId: string) => void;
}

const getSeverityColor = (severity: string): string => {
  switch (severity) {
    case 'catastrophic':
      return '#1a1a1a';
    case 'major':
      return '#1f1f1f';
    case 'moderate':
      return '#252525';
    case 'minor':
      return '#2a2a2a';
    default:
      return '#1a1a1a';
  }
};


const getEventIcon = (type: string): string => {
  switch (type) {
    case 'pirate_raid':
      return '[RAID]';
    case 'rift_storm':
      return '[STORM]';
    case 'sea_monster':
      return '[MONSTER]';
    case 'weather':
      return '[WEATHER]';
    case 'territory_claim':
      return '[TERRITORY]';
    case 'crew_event':
      return '[CREW]';
    default:
      return '[EVENT]';
  }
};

export const WorldEventNotification: React.FC<WorldEventNotificationProps> = ({
  events,
  onRemove,
}) => {
  const [visibleEvents, setVisibleEvents] = useState<WorldEvent[]>(events);

  useEffect(() => {
    setVisibleEvents(events);
  }, [events]);

  if (visibleEvents.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 90,
        right: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        zIndex: 4999,
        maxWidth: 280,
        pointerEvents: 'auto',
        fontFamily: 'var(--font-mono)',
      }}
    >
      {visibleEvents.map((event) => (
        <div
          key={event.id}
          role="button"
          tabIndex={0}
          onClick={() => onRemove(event.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onRemove(event.id);
            }
          }}
          style={{
            backgroundColor: getSeverityColor(event.severity),
            opacity: 0.8,
            color: '#ffffff',
            padding: '8px 12px',
            borderRadius: 0,
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLDivElement).style.transform = 'translateX(-2px)';
            (e.currentTarget as HTMLDivElement).style.boxShadow =
              '0 4px 12px rgba(0, 0, 0, 0.8)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLDivElement).style.transform = 'translateX(0)';
            (e.currentTarget as HTMLDivElement).style.boxShadow =
              '0 2px 8px rgba(0, 0, 0, 0.6)';
          }}
        >
          <div style={{ marginBottom: 2, lineHeight: 1.2 }}>
            <span style={{ marginRight: 6, fontSize: 10 }}>{getEventIcon(event.type)}</span>
            <span style={{ fontSize: 10 }}>{event.title}</span>
          </div>
          <div style={{ fontSize: 8, opacity: 0.85, lineHeight: 1.2 }}>{event.description}</div>
        </div>
      ))}
    </div>
  );
};
